/**
 * Terms of Service Route
 * Comprehensive terms of service page
 */

import { createFileRoute } from '@tanstack/react-router';
import { SEOHead } from '@/components/seo/SEOHead';
import { StructuredData, generateBreadcrumbSchema } from '@/components/seo/StructuredData';
import { PageLayout } from '@/components/layout/PageLayout';

function TermsOfServicePage() {
  const breadcrumbData = generateBreadcrumbSchema([
    { name: "Home", url: "https://invoicegenerated.com/" },
    { name: "Terms of Service", url: "https://invoicegenerated.com/terms" }
  ]);

  return (
    <>
      <SEOHead
        title="Terms of Service - Smart Invoice Generator"
        description="Terms of Service for Smart Invoice Generator. Read our terms and conditions for using our free invoice generation service."
        keywords="terms of service, terms and conditions, user agreement, legal terms, service terms, usage policies"
        canonical="https://invoicegenerated.com/terms"
      />
      <StructuredData data={breadcrumbData} />

      <PageLayout>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12 max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 sm:p-12">
            {/* Page Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Terms of Service
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
              Last Updated: February 4, 2026
            </p>

            {/* Introduction */}
            <section className="mb-10">
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Welcome to Smart Invoice Generator. These Terms of Service ("Terms") govern your use of our free invoice generation service. By accessing or using Smart Invoice Generator, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Please read these Terms carefully before using our service. If you do not agree with these Terms, you must not access or use Smart Invoice Generator.
              </p>
            </section>

            {/* Table of Contents */}
            <section className="mb-10 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Table of Contents
              </h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li><a href="#acceptance" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">1. Acceptance of Terms</a></li>
                <li><a href="#description" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">2. Service Description</a></li>
                <li><a href="#eligibility" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">3. Eligibility Requirements</a></li>
                <li><a href="#user-responsibilities" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">4. User Responsibilities</a></li>
                <li><a href="#intellectual-property" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">5. Intellectual Property Rights</a></li>
                <li><a href="#user-content" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">6. User-Generated Content</a></li>
                <li><a href="#prohibited-uses" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">7. Prohibited Uses</a></li>
                <li><a href="#disclaimers" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">8. Disclaimers and Warranties</a></li>
                <li><a href="#limitation" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">9. Limitation of Liability</a></li>
                <li><a href="#termination" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">10. Termination of Service</a></li>
                <li><a href="#governing-law" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">11. Governing Law and Disputes</a></li>
                <li><a href="#changes" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">12. Changes to Terms</a></li>
                <li><a href="#contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">13. Contact Information</a></li>
              </ul>
            </section>

            {/* 1. Acceptance of Terms */}
            <section id="acceptance" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                By accessing or using Smart Invoice Generator, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you are using our service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Your continued use of the service following any modifications to these Terms constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* 2. Service Description */}
            <section id="description" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                2. Service Description
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Smart Invoice Generator is a free, web-based service that enables users to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Create professional invoices using customizable templates</li>
                <li>Add company information, logos, and branding elements</li>
                <li>Calculate line items, taxes, discounts, and totals automatically</li>
                <li>Generate invoices in multiple currencies</li>
                <li>Export invoices as PDF or PNG files</li>
                <li>Save and manage invoice history locally in the browser</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Free Service:</strong> Smart Invoice Generator is provided completely free of charge. We do not charge subscription fees, per-invoice fees, or hidden costs.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                <strong>No Account Required:</strong> You can use our service without creating an account. However, certain features may require browser storage to function properly.
              </p>
            </section>

            {/* 3. Eligibility Requirements */}
            <section id="eligibility" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                3. Eligibility Requirements
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                To use Smart Invoice Generator, you must:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Be at least 13 years of age (or the minimum age in your jurisdiction)</li>
                <li>Have the legal capacity to enter into these Terms</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not be located in a country subject to comprehensive sanctions</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                If you are under 18 years old, you may only use our service with the consent and supervision of a parent or legal guardian.
              </p>
            </section>

            {/* 4. User Responsibilities */}
            <section id="user-responsibilities" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                4. User Responsibilities
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                As a user of Smart Invoice Generator, you agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Provide Accurate Information:</strong> Enter truthful, accurate, and complete information when creating invoices</li>
                <li><strong>Maintain Security:</strong> Keep your device and browser secure to protect access to your locally stored data</li>
                <li><strong>Comply with Laws:</strong> Use our service only for lawful purposes and in compliance with tax and financial regulations in your jurisdiction</li>
                <li><strong>Respect Intellectual Property:</strong> Do not infringe on the trademarks, copyrights, or other rights of others</li>
                <li><strong>Accept Responsibility:</strong> You are solely responsible for the content and accuracy of invoices you create</li>
              </ul>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 rounded-r">
                <p className="text-yellow-900 dark:text-yellow-200">
                  <strong>Important:</strong> Smart Invoice Generator is a tool to help you create invoices, but we cannot guarantee that your invoices will comply with all legal, tax, or financial requirements in your jurisdiction. Consult with a qualified professional for advice on your specific situation.
                </p>
              </div>
            </section>

            {/* 5. Intellectual Property Rights */}
            <section id="intellectual-property" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                5. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                All content, features, and functionality of Smart Invoice Generator are owned by us and are protected by international copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                What We Own
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Website design and layout</li>
                <li>Software code and algorithms</li>
                <li>Invoice templates and formatting</li>
                <li>Text, graphics, logos, and images</li>
                <li>Brand names and trademarks</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Your License to Use
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We grant you a limited, non-exclusive, non-transferable license to access and use Smart Invoice Generator for personal or business purposes. This license does not include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li>The right to reverse engineer or decompile our software</li>
                <li>The right to copy or modify our code or templates</li>
                <li>The right to use our brand or trademarks</li>
                <li>The right to remove or obscure attribution notices</li>
              </ul>
            </section>

            {/* 6. User-Generated Content */}
            <section id="user-content" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                6. User-Generated Content
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>You Retain Ownership:</strong> You retain full ownership of all invoices and content you create using our service. We do not claim any rights to your invoice data, designs, or content.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Your Responsibility:</strong> You are solely responsible for ensuring that your invoices:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Comply with applicable laws and regulations</li>
                <li>Do not infringe on third-party rights</li>
                <li>Are accurate and not misleading</li>
                <li>Include all required legal elements (tax IDs, business registration numbers, etc.)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                <strong>Our Rights:</strong> Since we don't store your invoice data, we do not use, display, or distribute your content. Your invoice data exists only on your device.
              </p>
            </section>

            {/* 7. Prohibited Uses */}
            <section id="prohibited-uses" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                7. Prohibited Uses
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You may not use Smart Invoice Generator to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Create fraudulent, deceptive, or misleading invoices</li>
                <li>Misrepresent your identity or business credentials</li>
                <li>Generate invoices for illegal goods or services</li>
                <li>Violate tax laws, financial regulations, or other legal requirements</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Distribute malware, viruses, or harmful code through our service</li>
                <li>Attempt to gain unauthorized access to our systems or other users' data</li>
                <li>Automate or script access to our service in a way that places unreasonable load on our servers</li>
                <li>Use our service for any purpose that is illegal or harmful</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We reserve the right to suspend or block access to our service for users who violate these prohibited uses.
              </p>
            </section>

            {/* 8. Disclaimers and Warranties */}
            <section id="disclaimers" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                8. Disclaimers and Warranties
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Smart Invoice Generator is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                We Do Not Warrant That:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>The service will be uninterrupted, timely, secure, or error-free</li>
                <li>The service will meet your specific requirements</li>
                <li>Generated invoices will be legally valid in your jurisdiction</li>
                <li>Calculations will be error-free (always review your invoices carefully)</li>
                <li>The service will be compatible with all devices or browsers</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Specifically Disclaimed Warranties:
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
                <li>Fitness for a particular purpose</li>
                <li>Non-infringement of third-party rights</li>
                <li>Title and ownership</li>
                <li>Accuracy of calculations or tax computations</li>
              </ul>

              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r mt-6">
                <p className="text-red-900 dark:text-red-200">
                  <strong>Critical Notice:</strong> Smart Invoice Generator is not accounting software, and we are not tax professionals or financial advisors. Always consult with qualified professionals for advice on your invoicing, tax, and financial reporting obligations.
                </p>
              </div>
            </section>

            {/* 9. Limitation of Liability */}
            <section id="limitation" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                9. Limitation of Liability
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                To the maximum extent permitted by applicable law, Smart Invoice Generator and its creators, contributors, and affiliates shall not be liable for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Lost profits, lost data, or business interruption</li>
                <li>Errors or inaccuracies in generated invoices</li>
                <li>Tax penalties, legal issues, or financial losses resulting from invoice errors</li>
                <li>System failures, data loss, or corruption</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Total Liability:</strong> Our total liability to you for all claims shall not exceed the amount you paid for the service (which is $0, since our service is free).
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability, so the above limitations may not apply to you.
              </p>
            </section>

            {/* 10. Termination of Service */}
            <section id="termination" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                10. Termination of Service
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Your Right to Terminate:</strong> You may stop using Smart Invoice Generator at any time, for any reason, without penalty.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong>Our Right to Terminate:</strong> We reserve the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Suspend or terminate your access if you violate these Terms</li>
                <li>Discontinue or modify the service at any time, with or without notice</li>
                <li>Limit access to the service in our sole discretion</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Upon termination, your right to use the service immediately ceases. We are not responsible for the loss of any data stored locally in your browser.
              </p>
            </section>

            {/* 11. Governing Law and Disputes */}
            <section id="governing-law" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                11. Governing Law and Disputes
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with applicable laws. Any disputes arising under these Terms shall be resolved through:
              </p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li><strong>Informal Resolution:</strong> We encourage you to contact us first to resolve any issues informally</li>
                <li><strong>Arbitration:</strong> If informal resolution fails, disputes may be resolved through binding arbitration (except where prohibited by law)</li>
                <li><strong>Courts:</strong> Certain disputes may be pursued in court, particularly those involving intellectual property infringement</li>
              </ol>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                <strong>Class Action Waiver:</strong> You agree to resolve disputes on an individual basis and waive any right to participate in class action lawsuits or class-wide arbitration.
              </p>
            </section>

            {/* 12. Changes to Terms */}
            <section id="changes" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                12. Changes to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on this page.
              </p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                We will provide notice of material changes by:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-6">
                <li>Updating the "Last Updated" date at the top of this page</li>
                <li>Displaying a prominent notice on our website</li>
                <li>Sending notifications if you've provided contact information</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Your continued use of the service after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* 13. Contact Information */}
            <section id="contact" className="mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                13. Contact Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:marfanw@gmail.com" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                      marfanw@gmail.com
                    </a>
                  </li>
                  <li>
                    <strong>Contact Form:</strong>{' '}
                    <a href="/contact" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                      Visit our Contact page
                    </a>
                  </li>
                </ul>
              </div>
            </section>

            {/* General Provisions */}
            <section className="mb-10 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                General Provisions
              </h2>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Smart Invoice Generator.</li>
                <li><strong>Severability:</strong> If any provision is deemed unenforceable, the remaining provisions remain in full force.</li>
                <li><strong>Waiver:</strong> Failure to enforce any provision does not constitute a waiver of that provision.</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our prior written consent.</li>
                <li><strong>No Third-Party Beneficiaries:</strong> These Terms do not create any rights for third parties.</li>
              </ul>
            </section>

            {/* Closing Statement */}
            <section className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
              <p className="text-gray-900 dark:text-gray-100 font-semibold text-lg mb-2">
                Thank You for Using Smart Invoice Generator
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                We're committed to providing a free, high-quality invoicing tool. These Terms help us maintain a safe and reliable service for everyone. If you have any questions or concerns, please don't hesitate to reach out.
              </p>
            </section>
          </div>
        </div>
      </PageLayout>
    </>
  );
}

export const Route = createFileRoute('/terms')({
  component: TermsOfServicePage,
});
